import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { type OfferPageRoom, type FlashSameRoomSelection, OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { setOfferPageRooms } from 'contexts/OfferPage/offerPageRoomActions'
import { getOfferPageRoomsTransitionState } from 'selectors/offerPage/roomChangeSelectors'
import usePackageSelectionBurnQuote from 'components/OfferPage/packageSelection/usePackageSelectionBurnQuote'
import { getPackageSelectionPaymentTotals, type PackageSelectionBurnItem, type PackageSelectionBurnQuote } from 'components/OfferPage/packageSelection/packageSelectionBurnQuote'
import { EmptyArray } from 'lib/array/arrayUtils'
import { clearFlashSameRoomSelection } from './flashSameRoomSelection'
import { isFlashSameRoomPackageAvailable } from 'lib/offer/flashPurchasePolicy'
import { getFlashPackageBurnItems, getFlashCombinedPackageBurnItems } from './flashPackageBurnItems'
import { getFlashPackageQuantity, getFlashSingleRoomQuantityLimit, resolveFlashPackageSelection, setFlashPackageQuantity, type FlashPackageViewsByOccupancy, type FlashPackageSelection } from './flashPackageSelection'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import type { OfferPageBurnResult } from 'hooks/useOfferPageBurnPoints'
import { floatify } from 'lib/maths/mathUtils'

export interface FlashPackageCardSelectionOptions {
  offer: App.HotelOffer
  view: App.OfferPackageOptionView
  viewsByOccupancy: FlashPackageViewsByOccupancy
  rooms: Array<OfferPageRoom>
  sameRoomSelection?: FlashSameRoomSelection
  selectedRoomTypeId?: string
  loading?: boolean
  /** Reuse the section's canonical single-room resolution instead of rebuilding it per card. */
  singleRoomSelection?: FlashPackageSelection
  /** Undefined means the section's quote is pending or points redemption is disabled. */
  singleRoomBurnQuote?: PackageSelectionBurnQuote
  requiresRates: boolean
  checkInventory: boolean
  burnUnavailable?: boolean
}

interface FlashPackageQuantityQuote {
  quantity: number
  price: number
  quote?: {
    memberPrice: number
    items: Array<PackageSelectionBurnItem>
    representedIds: Array<string>
  }
}

/** Product-owned quantity, inventory and points state; the card composes the visible content. */
export default function useFlashPackageCardSelection({ offer, view, viewsByOccupancy, rooms, sameRoomSelection, selectedRoomTypeId, loading = false, requiresRates, checkInventory, singleRoomSelection, singleRoomBurnQuote, burnUnavailable = false }: FlashPackageCardSelectionOptions) {
  const dispatch = useOfferPageDispatch()
  const roomsTransitionState = getOfferPageRoomsTransitionState(useContext(OfferPageStateContext))
  const intl = useIntl()
  const currency = useContext(CurrencyContext)
  const [quantityFailure, setQuantityFailure] = useState<string>()
  const multipleRooms = rooms.length > 1
  const sameRoomSelected = sameRoomSelection?.offerId === offer.id && sameRoomSelection.packageKey === view.package.uniqueKey
  const sameRoomReady = !loading && isFlashSameRoomPackageAvailable(view, rooms.length, requiresRates, checkInventory)
  const quantity = multipleRooms ? sameRoomSelected && sameRoomReady ? rooms.length : 0 : getFlashPackageQuantity(rooms, view)
  useEffect(() => { setQuantityFailure(undefined) }, [rooms, view, sameRoomSelection])
  const selection = useMemo(() => multipleRooms ? undefined : singleRoomSelection ?? resolveFlashPackageSelection({ rooms, viewsByOccupancy, requiresRates, checkInventory }), [checkInventory, multipleRooms, requiresRates, rooms, singleRoomSelection, viewsByOccupancy])
  const selectedView = multipleRooms ? sameRoomSelected && sameRoomReady ? view : undefined : selection?.allocations.find(allocation => allocation.view.package.uniqueKey === view.package.uniqueKey)?.view
  const quoteRooms = useMemo(() => multipleRooms || !selectedView ? rooms :
    selection?.allocations.filter(allocation => allocation.view.package.uniqueKey === selectedView.package.uniqueKey).map(allocation => allocation.room) ?? rooms,
  [multipleRooms, rooms, selectedView, selection?.allocations])
  const quantityQuotes = useMemo((): Array<FlashPackageQuantityQuote> => {
    const options: Array<FlashPackageQuantityQuote> = [{ quantity: 0, price: 0 }]
    if (multipleRooms) {
      if (!loading && isFlashSameRoomPackageAvailable(view, rooms.length, requiresRates, checkInventory)) {
        const prospectiveItems = getFlashCombinedPackageBurnItems(offer, view)
        options.push({ quantity: rooms.length, price: view.hotelTotal, quote: { memberPrice: view.hotelMemberTotal || view.hotelTotal, items: prospectiveItems, representedIds: [view.package.uniqueKey] } })
      }
    } else if (!loading) {
      const result = setFlashPackageQuantity({ rooms, viewsByOccupancy, packageView: view, quantity: 1, requiresRates, checkInventory })
      if (result.ok) {
        const prospectiveSelection = result.rooms === rooms && selection ? selection : resolveFlashPackageSelection({ rooms: result.rooms, viewsByOccupancy, requiresRates, checkInventory })
        const prospectiveView = prospectiveSelection.views[0]
        if (prospectiveView) {
          const maximum = getFlashSingleRoomQuantityLimit(prospectiveView, requiresRates, checkInventory)
          // Additional rooms initially repeat the current occupancy. These IDs are only for preview allocation.
          const allocations = Array.from({ length: maximum }, (_, index) => ({
            room: index === 0 ? result.rooms[0] : { ...result.rooms[0], id: `${rooms[0].id}:quantity:${index + 1}` },
            view: prospectiveView,
          }))
          for (let quantity = 1; quantity <= maximum; quantity++) {
            const selectedAllocations = allocations.slice(0, quantity)
            options.push({
              quantity,
              price: floatify(prospectiveView.hotelTotal * quantity),
              quote: {
                memberPrice: floatify((prospectiveView.hotelMemberTotal || prospectiveView.hotelTotal) * quantity),
                items: getFlashPackageBurnItems(offer, selectedAllocations),
                representedIds: selectedAllocations.map(allocation => allocation.room.id),
              },
            })
          }
        }
      }
    }
    return options
  }, [checkInventory, loading, multipleRooms, offer, requiresRates, rooms, selection, view, viewsByOccupancy])
  const prospectiveItems = quantityQuotes[quantityQuotes.length - 1]?.quote?.items ?? EmptyArray
  const reuseSectionQuote = !!singleRoomSelection && quantity > 0 && quantityQuotes.length <= 2
  const representedIds = useMemo(() => selectedView ? [multipleRooms ? selectedView.package.uniqueKey : rooms[0].id] : [], [multipleRooms, rooms, selectedView])
  const { quote: localBurnQuote, enabled: localBurnEnabled, fetching: burnFetching } = usePackageSelectionBurnQuote(reuseSectionQuote ? EmptyArray : prospectiveItems, { disabled: reuseSectionQuote })
  const prospectiveBurnQuote = reuseSectionQuote ? singleRoomBurnQuote : localBurnQuote
  const burnQuote = selectedView ? (!multipleRooms && singleRoomSelection ? singleRoomBurnQuote : prospectiveBurnQuote) : undefined
  const quantityPrices = useMemo(() => quantityQuotes.map(option => {
    const optionBurnQuote = !multipleRooms && option.quantity === 1 && singleRoomSelection && quantity > 0 ? singleRoomBurnQuote : prospectiveBurnQuote
    return {
      ...option,
      quote: option.quote && {
        ...option.quote,
        payment: optionBurnQuote && getPackageSelectionPaymentTotals(optionBurnQuote, option.quote.representedIds),
        fetching: burnFetching,
      },
    }
  }), [burnFetching, multipleRooms, prospectiveBurnQuote, quantity, quantityQuotes, singleRoomBurnQuote, singleRoomSelection])
  const purchaseDisabled = burnUnavailable || (!!localBurnEnabled && (quantity > 0 ? !burnQuote : prospectiveItems.length > 0 && !prospectiveBurnQuote))
  const selectionPricing = useMemo(() => ({
    quantity,
    price: selectedView?.hotelTotal ?? 0,
    memberPrice: selectedView?.hotelMemberTotal || selectedView?.hotelTotal || 0,
    payment: burnQuote && getPackageSelectionPaymentTotals(burnQuote, representedIds),
  }), [burnQuote, quantity, representedIds, selectedView])
  const roomSelected = multipleRooms
    ? selectedRoomTypeId === view.package.roomType.id
    : !!selection?.views.some(selected => selected?.package.roomType.id === view.package.roomType.id)
  const { currentRegionCode } = useContext(GeoContext)
  const calculationRequests = useMemo(() => [generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView: view, currentRegionCode, nights: view.package.duration })], [currentRegionCode, offer, view])
  const burnResult = useMemo((): OfferPageBurnResult => {
    if (!burnQuote || !selectedView) return { enabled: false }
    const priceInPoints = representedIds.reduce((total, id) => total + (burnQuote.byId.get(id)?.regularMaximum ?? 0), 0)
    const pointsToBurn = selectionPricing.payment?.regular.points ?? 0
    return { enabled: pointsToBurn > 0, priceInPoints, pointsToBurn, hideZeroCash: selectionPricing.payment?.regular.cash === 0 }
  }, [burnQuote, representedIds, selectedView, selectionPricing.payment])

  const onQuantityChange = useCallback((nextQuantity: number) => {
    if (multipleRooms) {
      if (nextQuantity === 0) {
        if (sameRoomSelected) dispatch(clearFlashSameRoomSelection(rooms))
        setQuantityFailure(undefined)
        return
      }
      if (nextQuantity === rooms.length && !loading && isFlashSameRoomPackageAvailable(view, rooms.length, requiresRates, checkInventory)) {
        dispatch({ type: OfferPageActions.setFlashSameRoomSelection, selection: { offerId: offer.id, packageKey: view.package.uniqueKey } })
        setQuantityFailure(undefined)
        return
      }
    } else if (!loading && (nextQuantity === 0 || (Number.isInteger(nextQuantity) && nextQuantity > 0 && nextQuantity <= getFlashSingleRoomQuantityLimit(view, requiresRates, checkInventory)))) {
      if (nextQuantity > 1) {
        const result = setFlashPackageQuantity({ rooms, viewsByOccupancy, packageView: view, quantity: 1, requiresRates, checkInventory })
        if (result.ok) {
          const firstRoom = result.rooms[0]
          const nextRooms = [firstRoom, ...Array.from({ length: nextQuantity - 1 }, () => generateOfferPageRoom({ ...firstRoom, id: undefined }))]
          dispatch({
            type: OfferPageActions.toggleFilter,
            filter: 'capacity',
            flashQuantityDraft: {
              rooms: nextRooms,
              selection: { offerId: offer.id, packageKey: view.package.uniqueKey },
            },
          })
          setQuantityFailure(undefined)
          return
        }
      }
      const result = setFlashPackageQuantity({ rooms, viewsByOccupancy, packageView: view, quantity: nextQuantity, requiresRates, checkInventory })
      if (result.ok) {
        setQuantityFailure(undefined)
        if (result.rooms !== rooms) dispatch(setOfferPageRooms(roomsTransitionState, result.rooms))
        return
      }
    }
    setQuantityFailure(intl.formatMessage({
      id: '57mHcrRXZTJb0XGNYf5p',
      defaultMessage: 'This quantity is no longer available for your search. Review your selection and try again.',
      description: 'Visible feedback when a requested Flash package quantity cannot be applied, while preserving every guest and existing selection',
    }))
  }, [checkInventory, dispatch, intl, loading, multipleRooms, offer.id, requiresRates, rooms, roomsTransitionState, sameRoomSelected, view, viewsByOccupancy])

  const quantityQuoteKey = useMemo(() => JSON.stringify([
    currency, rooms, sameRoomSelection, loading, checkInventory, requiresRates,
    view.package.uniqueKey, view.hotelTotal, view.hotelMemberTotal, view.pricing, view.memberPricing,
    view.availableRooms, view.capacityValid, view.hotelAvailable, view.hasAvailableRates,
    prospectiveBurnQuote?.regular, prospectiveBurnQuote?.member,
  ]), [prospectiveBurnQuote?.member, prospectiveBurnQuote?.regular, checkInventory, currency, loading, requiresRates, rooms, sameRoomSelection, view])

  return useMemo(() => ({ quantity, quantityFailure, onQuantityChange, roomSelected, selectionPricing, quoteRooms, burnResult, calculationRequests, quantityQuotes: quantityPrices, quantityQuoteKey, purchaseDisabled }), [quantity, quantityFailure, onQuantityChange, roomSelected, selectionPricing, quoteRooms, burnResult, calculationRequests, quantityPrices, quantityQuoteKey, purchaseDisabled])
}
